<template>
  <div>
    <!--    Breadcrumbs-->
    <breadcrumb style="margin-left: -15px">
      <breadcrumb-item>
        <a href="/games/history?count=10&page=1">Event history</a>
      </breadcrumb-item>
      <breadcrumb-item>
        <span>{{gameInstance.name}}</span>
      </breadcrumb-item>
    </breadcrumb>

    <!--    Loader-->
    <loading-panel :loading="loading"></loading-panel>

    <div class="row">
      <div class="col-lg-12">
        <card class="card-subcategories" card-body-classes="table-full-width">
          <div class="gi-header" style="margin-bottom: 20px">
            <img v-if="gameInstance.url" class="gi-header__img" v-twin-src="gameInstance.url" alt="Game image"/>
            <div v-else class="gi-header__img-placeholder"><i class="tim-icons icon-controller"></i></div>
            <div class="gi-header__info">
              <h3 class="gi-header__title">{{gameInstance.name}}</h3>
              <div v-if="gameInstance.transaction_id" class="gi-header__meta">
                <span class="gi-header__meta-item">
                  <i class="tim-icons icon-cart"></i>
                  Transaction #{{gameInstance.order_number}}
                  <router-link
                    :to="'/purchases/transactions/details?id=' + gameInstance.transaction_id"
                    custom
                    v-slot="{ href }"
                  >
                    <a :href="href" target="_blank" rel="noopener">
                      <i class="tim-icons el-icon-top-right"></i>
                    </a>
                  </router-link>
                </span>
              </div>
            
    <base-alert-dialog :show="alertDialog.show"
                       :title="alertDialog.title"
                       :message="alertDialog.message"
                       :variant="alertDialog.variant"
                       :show-cancel="alertDialog.showCancel"
                       :confirm-text="alertDialog.confirmText"
                       :cancel-text="alertDialog.cancelText"
                       :confirm-variant="alertDialog.confirmVariant"
                       @confirm="resolveAlert(true)"
                       @cancel="resolveAlert(false)" />
</div>
            <div class="gi-header__actions">
              <template v-if="!editingGame">
                <el-tooltip content="Edit event" effect="light" :open-delay="300" placement="top">
                  <button v-if="hasPermissions('game_history_update')"
                          class="gi-accordion-action-btn"
                          @click="editGameInstance()">
                    <i class="tim-icons icon-pencil"></i> Edit
                  </button>
                </el-tooltip>
                <el-tooltip content="Delete event" effect="light" :open-delay="300" placement="top">
                  <button v-if="hasPermissions('game_history_delete')"
                          class="gi-accordion-action-btn"
                          style="border-color: #fd5d93; color: #fd5d93"
                          @click="deleteGameInstance()">
                    <i class="tim-icons icon-trash-simple"></i>
                  </button>
                </el-tooltip>
              </template>
              <template v-else>
                <button class="gi-accordion-action-btn gi-accordion-action-btn--save"
                        @click="saveGameInstance()">
                  <i class="tim-icons icon-check-2"></i> Save
                </button>
                <button class="gi-accordion-action-btn"
                        @click="cancelEditingGame()">
                  Cancel
                </button>
              </template>
            </div>
          </div>
          <!-- Stat cards row -->
          <div class="row gi-equal-height gi-equal-height--nowrap" :class="{ 'gi-stat-row--five': pacingSummary }">
            <div class="col-lg-3 col-sm-6">
              <div class="gi-section gi-stat-card" :class="{'gi-section--editing': editingGame}">
                <div class="gi-stat-card__icon"><i class="tim-icons icon-calendar-60"></i></div>
                <div class="gi-stat-card__content">
                  <span class="gi-section__label">Booking time</span>
                  <div v-if="!editingGame" class="gi-stat-value">
                    {{getTime(gameInstance.booking_start_time)}}
                    <small>{{getDate(gameInstance.booking_date)}}</small>
                  </div>
                </div>
                <div v-if="editingGame" style="width: 100%">
                  <base-input :error="errs.booking_time" style="margin-bottom: 0">
                    <el-time-picker v-model="editing.booking_time"
                      :format="picker_time_format" placeholder="HH:MM"></el-time-picker>
                  </base-input>
                </div>
              </div>
            </div>
            <div class="col-lg-3 col-sm-6">
              <div class="gi-section gi-stat-card" :class="{'gi-section--editing': editingGame}">
                <div class="gi-stat-card__icon"><i class="tim-icons icon-triangle-right-17"></i></div>
                <div class="gi-stat-card__content">
                  <span class="gi-section__label">Start time</span>
                  <div v-if="!editingGame" class="gi-stat-value">
                    <template v-if="gameInstance.start_time">
                      {{getTime(gameInstance.start_time)}}
                      <small>{{getDate(gameInstance.start_time)}}</small>
                    </template>
                    <template v-else>
                      <span class="gi-stat-value__missing">Not recorded</span>
                      <small v-if="hasPermissions('game_history_update')">edit the event to set it</small>
                    </template>
                  </div>
                </div>
                <div v-if="editingGame" style="width: 100%">
                  <base-input :error="errs.start_time" style="margin-bottom: 8px">
                    <el-date-picker :editable="false" :format="day_of_week + date_format_cal"
                      v-model="editing.start_date" :picker-options="{ firstDayOfWeek: first_day_of_week }"></el-date-picker>
                  </base-input>
                  <base-input :error="errs.start_time" style="margin-bottom: 0">
                    <el-time-picker v-model="editing.start_time"
                      :format="picker_time_format" placeholder="HH:MM"></el-time-picker>
                  </base-input>
                </div>
              </div>
            </div>
            <div class="col-lg-3 col-sm-6">
              <div class="gi-section gi-stat-card" :class="{'gi-section--editing': editingGame}">
                <div class="gi-stat-card__icon"><i class="tim-icons icon-trophy"></i></div>
                <div class="gi-stat-card__content">
                  <span class="gi-section__label">End time</span>
                  <div v-if="!editingGame" class="gi-stat-value">
                    {{getTime(gameInstance.end_time)}}
                    <small>{{getDate(gameInstance.end_time)}}</small>
                  </div>
                </div>
                <div v-if="editingGame" style="width: 100%">
                  <base-input :error="errs.end_time" style="margin-bottom: 8px">
                    <el-date-picker :editable="false" :format="day_of_week + date_format_cal"
                      v-model="editing.end_date" :picker-options="{ firstDayOfWeek: first_day_of_week }"></el-date-picker>
                  </base-input>
                  <base-input :error="errs.end_time" style="margin-bottom: 0">
                    <el-time-picker v-model="editing.end_time"
                      :format="picker_time_format" placeholder="HH:MM"></el-time-picker>
                  </base-input>
                </div>
              </div>
            </div>
            <div class="col-lg-3 col-sm-6">
              <div class="gi-section gi-stat-card" :class="{'gi-section--editing': editingGame}">
                <div class="gi-stat-card__icon"><i class="tim-icons icon-time-alarm"></i></div>
                <div class="gi-stat-card__content">
                  <span class="gi-section__label">Total time</span>
                  <div class="gi-stat-value">{{getTotalTime(puzzleInstances)}}</div>
                </div>
              </div>
            </div>
            <div v-if="pacingSummary" class="col-lg-3 col-sm-6">
              <div class="gi-section gi-stat-card" :class="{'gi-section--editing': editingGame}">
                <div class="gi-stat-card__icon"><i class="tim-icons icon-spaceship"></i></div>
                <div class="gi-stat-card__content">
                  <span class="gi-section__label">Pacing</span>
                  <div class="gi-stat-value" :class="'gi-stat-value--pace-' + pacingSummary.status">
                    {{ pacingStatusLabel }}
                    <small v-if="pacingSummary.totalDelta !== null">{{ formatPaceDelta(pacingSummary.totalDelta) }}</small>
                    <small>last {{ pacingSummary.sampleSize }} events</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Metrics & notes row -->
          <div class="row gi-equal-height">
            <div class="col-lg-4">
              <div class="gi-section" :class="{'gi-section--editing': editingGame}">
                <div class="gi-section__header">
                  <span class="gi-section__label gi-section__label--lg"><i class="tim-icons icon-settings-gear-63"></i> Session</span>
                </div>
                <div class="gi-details">
                  <div class="gi-detail-row">
                    <span class="gi-detail-row__label">Host</span>
                    <span v-if="!editingGame" class="gi-detail-row__value">{{gameInstance.first_name}} {{gameInstance.last_name}}</span>
                    <div v-else class="gi-detail-row__edit">
                      <el-select v-model="selectedGameMaster" class="select-primary" name="group"
                        placeholder="Host" value-key="id" style="max-width: 280px">
                        <el-option v-for="u in company_users" class="select-primary"
                          :label="u.first_name + ' ' + u.last_name + ' (' + u.email + ')'"
                          :value="{id: u.id, first_name: u.first_name, last_name: u.last_name, email: u.email}"
                          :key="'A' + u.id"></el-option>
                      </el-select>
                    </div>
                  </div>
                  <div v-if="hasFieldEnabled(7)" class="gi-detail-row">
                    <span class="gi-detail-row__label">Corporate booking</span>
                    <span class="gi-detail-row__value">
                      <el-switch active-color="#e14eca" :disabled="!editingGame"
                        v-model="gameInstance.is_corporate_booking" />
                    </span>
                  </div>
                  <div v-if="hasFieldEnabled(4)" class="gi-detail-row">
                    <span class="gi-detail-row__label">Total interactions</span>
                    <span v-if="!editingGame" class="gi-detail-row__value gi-detail-row__value--success">{{getTotalClues(puzzleInstances)}}</span>
                    <div v-else class="gi-detail-row__edit">
                      <base-input placeholder="Number of interactions" style="margin-bottom: 0; max-width: 120px"
                        v-model.number="gameInstance.number_of_hints"></base-input>
                    </div>
                  </div>
                  <div v-if="hasFieldEnabled(5)" class="gi-detail-row">
                    <span class="gi-detail-row__label">Total issues</span>
                    <span v-if="!editingGame" class="gi-detail-row__value gi-detail-row__value--success">{{getTotalBreakages(puzzleInstances)}}</span>
                    <div v-else class="gi-detail-row__edit">
                      <base-input placeholder="Number of issues" style="margin-bottom: 0; max-width: 120px"
                        v-model.number="gameInstance.number_of_breaks"></base-input>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-lg-4">
              <div class="gi-section" :class="{'gi-section--editing': editingGame}">
                <div class="gi-section__header">
                  <span class="gi-section__label gi-section__label--lg"><i class="tim-icons icon-chart-bar-32"></i> Performance</span>
                </div>
                <div class="gi-details">
                  <div v-if="hasFieldEnabled(6)" class="gi-detail-row">
                    <span class="gi-detail-row__label">Event completed</span>
                    <span class="gi-detail-row__value">
                      <el-switch active-color="#e14eca" :disabled="!editingGame"
                        v-model="gameInstance.completed" />
                    </span>
                  </div>
                  <div v-if="puzzleInstances.length > 0" class="gi-detail-row">
                    <span class="gi-detail-row__label">Percent completion</span>
                    <span class="gi-detail-row__value gi-detail-row__value--success">{{getPercentCompletion(puzzleInstances)}}</span>
                  </div>
                  <div v-if="!exceed_time" class="gi-detail-row">
                    <span class="gi-detail-row__label">Time remaining</span>
                    <span class="gi-detail-row__value gi-detail-row__value--success">{{getMinutesRemaining(gameInstance)}}</span>
                  </div>
                  <div v-if="exceed_time" class="gi-detail-row">
                    <span class="gi-detail-row__label">Extra time spent</span>
                    <span class="gi-detail-row__value gi-detail-row__value--danger">{{getMinutesRemaining(gameInstance)}}</span>
                  </div>
                  <div v-if="hasFieldEnabled(3)" class="gi-detail-row">
                    <span class="gi-detail-row__label">Custom time</span>
                    <span v-if="!editingGame" class="gi-detail-row__value gi-detail-row__value--success">{{formatCustomTime(gameInstance.custom_time)}}</span>
                    <div v-else class="gi-detail-row__edit">
                      <base-input class="custom-time-input" placeholder="MM:SS" inputmode="numeric" maxlength="8" style="margin-bottom: 0; max-width: 150px"
                        :value="customTimeInput" @input="onCustomTimeInput" @blur="onCustomTimeBlur"></base-input>
                    </div>
                  </div>
                  <div v-if="gameInstance.timer_value" class="gi-detail-row">
                    <span class="gi-detail-row__label">Final timer value</span>
                    <span class="gi-detail-row__value gi-detail-row__value--success">{{gameInstance.timer_value}}</span>
                  </div>
                  <div v-if="gameInstance.display_timer_value" class="gi-detail-row">
                    <span class="gi-detail-row__label">Customer screen timer</span>
                    <span class="gi-detail-row__value gi-detail-row__value--success">{{gameInstance.display_timer_value}}</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-lg-4">
              <div class="gi-section" :class="{'gi-section--editing': editingGame}">
                <div class="gi-section__header">
                  <span class="gi-section__label gi-section__label--lg"><i class="tim-icons icon-paper"></i> Notes and tags</span>
                </div>
                <div class="gi-details">
                  <div class="gi-detail-row gi-detail-row--stacked">
                    <span class="gi-detail-row__label">Pre-event notes</span>
                    <template v-if="!editingGame">
                      <span v-if="gameInstance.pre_game_notes" class="gi-notes-text">{{gameInstance.pre_game_notes}}</span>
                      <span v-else class="gi-detail-row__value gi-detail-row__value--empty">N/A</span>
                    </template>
                    <textarea v-else class="form-control" placeholder="Notes..."
                      v-model="gameInstance.pre_game_notes" rows="3"></textarea>
                  </div>
                  <div class="gi-detail-row gi-detail-row--stacked">
                    <span class="gi-detail-row__label">Post-event notes</span>
                    <template v-if="!editingGame">
                      <span v-if="gameInstance.post_game_notes" class="gi-notes-text">{{gameInstance.post_game_notes}}</span>
                      <span v-else class="gi-detail-row__value gi-detail-row__value--empty">N/A</span>
                    </template>
                    <textarea v-else class="form-control" placeholder="Notes..."
                      v-model="gameInstance.post_game_notes" rows="3"></textarea>
                  </div>
                  <!-- Tags on the session's booking (saves immediately, same
                       pattern as the booking drawer) -->
                  <div v-if="gameInstance.booking_slot_id" class="gi-detail-row gi-detail-row--stacked">
                    <span class="gi-detail-row__label">Tags</span>
                    <div class="tags-row">
                      <template v-if="tagsLoading">
                        <span class="skel skel--chip"></span>
                        <span class="skel skel--chip"></span>
                      </template>
                      <template v-else>
                      <span
                        v-for="tag in bookingTags"
                        :key="'GIT' + tag.id"
                        class="tag-chip text-white"
                        :class="{ 'tag-chip--archived': tag.is_archived }"
                        :style="tagChipStyle(tag.color)"
                      >
                        {{ tag.name }}
                        <button
                          class="tag-chip__remove"
                          :disabled="savingTags"
                          :aria-label="'Remove tag ' + tag.name"
                          @click="removeTag(tag)"
                        >
                          <i class="tim-icons icon-simple-remove"></i>
                        </button>
                      </span>
                      <el-dropdown trigger="click" @command="addTag">
                        <span class="tag-chip tag-chip--add">
                          <i v-if="savingTags" class="el-icon-loading mr-1" style="color: #e14eca;"></i>
                          <i v-else class="tim-icons icon-simple-add mr-1"></i>
                          Add tag
                        </span>
                        <el-dropdown-menu slot="dropdown">
                          <el-dropdown-item v-for="tag in availableTags" :key="'GAT' + tag.id" :command="tag.id">
                            <span class="tag-chip__dot mr-1" :style="{ backgroundColor: tag.color }"></span>
                            {{ tag.name }}
                          </el-dropdown-item>
                          <el-dropdown-item v-if="availableTags.length === 0" disabled>No more tags</el-dropdown-item>
                        </el-dropdown-menu>
                      </el-dropdown>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </card>

      </div>

      <!-- Objectives -->
      <div ref="puzzles" class="col-lg-12" v-if="puzzleInstances.length > 0">
        <card card-body-classes="table-full-width">
          <h4 slot="header" class="card-title">Objectives</h4>

          <!-- Game timeline -->
          <div class="gi-timeline">

            <!-- Start node -->
            <div class="gi-timeline-node gi-timeline-node--start">
              <div class="gi-timeline-dot gi-timeline-dot--start"></div>
              <span class="gi-timeline-time">{{ getTime(gameInstance.start_time) }}</span>
              <span class="gi-timeline-node-badge gi-timeline-node-badge--start">Event start</span>
            </div>

            <!-- Completed objectives before event end -->
            <div v-for="entry in completedBeforeEnd"
                 :key="'TC' + entry.flatIndex"
                 class="gi-timeline-node gi-timeline-node--completed">
              <div class="gi-timeline-dot gi-timeline-dot--completed"></div>
              <span class="gi-timeline-time">{{ getMinutesSinceStart(entry.puzzle) }}</span>
              <div class="gi-timeline-card"
                   :class="{
                     'gi-timeline-card--editing': editingPuzzleIndex === entry.flatIndex,
                     'gi-timeline-card--child': entry.isChild
                   }">
                <div class="gi-timeline-card__header">
                  <div class="gi-timeline-card__header-left">
                    <span class="gi-accordion-badge gi-accordion-badge--completed">
                      {{ getTime(entry.puzzle.solve_time) }}
                    </span>
                    <span class="gi-timeline-card__name">{{ entry.puzzle.puzzle_name }}</span>
                  </div>
                  <div class="gi-timeline-card__header-right">
                    <span v-if="pacingChip(entry.puzzle)"
                          class="gi-accordion-badge"
                          :class="'gi-accordion-badge--pace-' + pacingChip(entry.puzzle).band">
                      {{ formatPaceDelta(pacingChip(entry.puzzle).delta) }}
                    </span>
                    <span v-if="parseInt(entry.puzzle.hints_given_count) > 0"
                          class="gi-accordion-badge gi-accordion-badge--hints">
                      {{ entry.puzzle.hints_given_count }} interaction{{ parseInt(entry.puzzle.hints_given_count) !== 1 ? 's' : '' }}
                    </span>
                    <span v-if="entry.puzzle.breakage_occurred"
                          class="gi-accordion-badge gi-accordion-badge--issues">
                      {{ typeof entry.puzzle.breakage_occurred === 'number' ? entry.puzzle.breakage_occurred : 1 }} issue{{ entry.puzzle.breakage_occurred > 1 ? 's' : '' }}
                    </span>
                    <div class="gi-accordion-actions">
                      <button v-if="editingPuzzleIndex !== entry.flatIndex && hasPermissions('game_history_update')"
                              class="gi-accordion-action-btn"
                              @click="editPuzzle(entry.flatIndex)">
                        <i class="tim-icons icon-pencil"></i>
                      </button>
                      <button v-if="editingPuzzleIndex === entry.flatIndex"
                              class="gi-accordion-action-btn gi-accordion-action-btn--save"
                              @click="savePuzzleInstance()">
                        <i class="tim-icons icon-check-2"></i> Save
                      </button>
                      <button v-if="editingPuzzleIndex === entry.flatIndex"
                              class="gi-accordion-action-btn"
                              @click="cancelEditingPuzzles()">
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Notes -->
                <div v-if="editingPuzzleIndex !== entry.flatIndex && (entry.puzzle.hints_given_description || (entry.puzzle.breakage_occurred && entry.puzzle.breakage_description))"
                     class="gi-timeline-card__notes">
                  <span v-if="entry.puzzle.hints_given_description" class="gi-timeline-card__note">
                    <i class="tim-icons icon-chat-33"></i> {{ entry.puzzle.hints_given_description }}
                  </span>
                  <span v-if="entry.puzzle.breakage_occurred && entry.puzzle.breakage_description" class="gi-timeline-card__note gi-timeline-card__note--issue">
                    <i class="tim-icons icon-alert-circle-exc"></i> {{ entry.puzzle.breakage_description }}
                  </span>
                </div>

                <!-- Edit form -->
                <div v-if="editingPuzzleIndex === entry.flatIndex" class="gi-timeline-card__body">
                  <div class="gi-accordion-edit-row">
                    <div>
                      <label>Completed</label>
                      <el-switch v-model="entry.puzzle.is_completed" :active-value="1" :inactive-value="0" active-color="#e14eca"></el-switch>
                    </div>
                  </div>
                  <div class="gi-accordion-edit-row" v-if="entry.puzzle.is_completed">
                    <div>
                      <label>Completion date</label>
                      <base-input :error="errs.solve_date">
                        <el-date-picker :editable="false" :format="day_of_week + date_format_cal"
                                        v-model="entry.puzzle.solve_time_d"
                                        placeholder="Date"
                                        :picker-options="{ firstDayOfWeek: first_day_of_week }">
                        </el-date-picker>
                      </base-input>
                    </div>
                    <div>
                      <label>Completion time</label>
                      <base-input :error="errs.solve_time">
                        <el-time-picker v-model="entry.puzzle.solve_time_t"
                                        :format="picker_time_format"
                                        placeholder="Time">
                        </el-time-picker>
                      </base-input>
                    </div>
                  </div>
                  <div class="gi-accordion-edit-row">
                    <div>
                      <label>Interaction count</label>
                      <base-input style="width: 150px">
                        <el-input-number size="mini" :min="0"
                                         v-model="entry.puzzle.hints_given_count">
                        </el-input-number>
                      </base-input>
                    </div>
                  </div>
                  <div class="gi-accordion-edit-row">
                    <div style="flex: 1">
                      <label>Interactions given</label>
                      <textarea class="form-control"
                                placeholder="List the interactions..."
                                v-model="entry.puzzle.hints_given_description"
                                rows="3"></textarea>
                    </div>
                  </div>
                  <div class="gi-accordion-edit-row">
                    <div style="flex: 1">
                      <label>Issues</label>
                      <textarea class="form-control"
                                placeholder="Describe the issues..."
                                v-model="entry.puzzle.breakage_description"
                                rows="3"></textarea>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- End node -->
            <div v-if="gameInstance.end_time" class="gi-timeline-node gi-timeline-node--end">
              <div class="gi-timeline-dot gi-timeline-dot--end"></div>
              <span class="gi-timeline-time">{{ getTime(gameInstance.end_time) }}</span>
              <span class="gi-timeline-node-badge gi-timeline-node-badge--end">Event end</span>
            </div>

            <!-- Completed objectives after event end (overtime) -->
            <div v-for="entry in completedAfterEnd"
                 :key="'TO' + entry.flatIndex"
                 class="gi-timeline-node gi-timeline-node--completed gi-timeline-node--overtime">
              <div class="gi-timeline-dot gi-timeline-dot--overtime"></div>
              <span class="gi-timeline-time">{{ getMinutesSinceStart(entry.puzzle) }}</span>
              <div class="gi-timeline-card gi-timeline-card--overtime"
                   :class="{
                     'gi-timeline-card--editing': editingPuzzleIndex === entry.flatIndex,
                     'gi-timeline-card--child': entry.isChild
                   }">
                <div class="gi-timeline-card__header">
                  <div class="gi-timeline-card__header-left">
                    <span class="gi-accordion-badge gi-accordion-badge--completed">
                      {{ getTime(entry.puzzle.solve_time) }}
                    </span>
                    <span class="gi-timeline-card__name">{{ entry.puzzle.puzzle_name }}</span>
                  </div>
                  <div class="gi-timeline-card__header-right">
                    <span v-if="pacingChip(entry.puzzle)"
                          class="gi-accordion-badge"
                          :class="'gi-accordion-badge--pace-' + pacingChip(entry.puzzle).band">
                      {{ formatPaceDelta(pacingChip(entry.puzzle).delta) }}
                    </span>
                    <span v-if="parseInt(entry.puzzle.hints_given_count) > 0"
                          class="gi-accordion-badge gi-accordion-badge--hints">
                      {{ entry.puzzle.hints_given_count }} interaction{{ parseInt(entry.puzzle.hints_given_count) !== 1 ? 's' : '' }}
                    </span>
                    <span v-if="entry.puzzle.breakage_occurred"
                          class="gi-accordion-badge gi-accordion-badge--issues">
                      {{ typeof entry.puzzle.breakage_occurred === 'number' ? entry.puzzle.breakage_occurred : 1 }} issue{{ entry.puzzle.breakage_occurred > 1 ? 's' : '' }}
                    </span>
                    <div class="gi-accordion-actions">
                      <button v-if="editingPuzzleIndex !== entry.flatIndex && hasPermissions('game_history_update')"
                              class="gi-accordion-action-btn"
                              @click="editPuzzle(entry.flatIndex)">
                        <i class="tim-icons icon-pencil"></i>
                      </button>
                      <button v-if="editingPuzzleIndex === entry.flatIndex"
                              class="gi-accordion-action-btn gi-accordion-action-btn--save"
                              @click="savePuzzleInstance()">
                        <i class="tim-icons icon-check-2"></i> Save
                      </button>
                      <button v-if="editingPuzzleIndex === entry.flatIndex"
                              class="gi-accordion-action-btn"
                              @click="cancelEditingPuzzles()">
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Notes -->
                <div v-if="editingPuzzleIndex !== entry.flatIndex && (entry.puzzle.hints_given_description || (entry.puzzle.breakage_occurred && entry.puzzle.breakage_description))"
                     class="gi-timeline-card__notes">
                  <span v-if="entry.puzzle.hints_given_description" class="gi-timeline-card__note">
                    <i class="tim-icons icon-chat-33"></i> {{ entry.puzzle.hints_given_description }}
                  </span>
                  <span v-if="entry.puzzle.breakage_occurred && entry.puzzle.breakage_description" class="gi-timeline-card__note gi-timeline-card__note--issue">
                    <i class="tim-icons icon-alert-circle-exc"></i> {{ entry.puzzle.breakage_description }}
                  </span>
                </div>

                <!-- Edit form -->
                <div v-if="editingPuzzleIndex === entry.flatIndex" class="gi-timeline-card__body">
                  <div class="gi-accordion-edit-row">
                    <div>
                      <label>Completed</label>
                      <el-switch v-model="entry.puzzle.is_completed" :active-value="1" :inactive-value="0" active-color="#e14eca"></el-switch>
                    </div>
                  </div>
                  <div class="gi-accordion-edit-row" v-if="entry.puzzle.is_completed">
                    <div>
                      <label>Completion date</label>
                      <base-input :error="errs.solve_date">
                        <el-date-picker :editable="false" :format="day_of_week + date_format_cal"
                                        v-model="entry.puzzle.solve_time_d"
                                        placeholder="Date"
                                        :picker-options="{ firstDayOfWeek: first_day_of_week }">
                        </el-date-picker>
                      </base-input>
                    </div>
                    <div>
                      <label>Completion time</label>
                      <base-input :error="errs.solve_time">
                        <el-time-picker v-model="entry.puzzle.solve_time_t"
                                        :format="picker_time_format"
                                        placeholder="Time">
                        </el-time-picker>
                      </base-input>
                    </div>
                  </div>
                  <div class="gi-accordion-edit-row">
                    <div>
                      <label>Interaction count</label>
                      <base-input style="width: 150px">
                        <el-input-number size="mini" :min="0"
                                         v-model="entry.puzzle.hints_given_count">
                        </el-input-number>
                      </base-input>
                    </div>
                  </div>
                  <div class="gi-accordion-edit-row">
                    <div style="flex: 1">
                      <label>Interactions given</label>
                      <textarea class="form-control"
                                placeholder="List the interactions..."
                                v-model="entry.puzzle.hints_given_description"
                                rows="3"></textarea>
                    </div>
                  </div>
                  <div class="gi-accordion-edit-row">
                    <div style="flex: 1">
                      <label>Issues</label>
                      <textarea class="form-control"
                                placeholder="Describe the issues..."
                                v-model="entry.puzzle.breakage_description"
                                rows="3"></textarea>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Incomplete objectives -->
          <div v-if="incompletePuzzles.length > 0" class="gi-incomplete-section">
            <h5 class="gi-incomplete-section__title">Not completed</h5>
            <div v-for="entry in incompletePuzzles"
                 :key="'TI' + entry.flatIndex"
                 class="gi-accordion-item gi-accordion-item--incomplete"
                 :class="{
                   'gi-accordion-item--child': entry.isChild,
                   'gi-accordion-item--editing': editingPuzzleIndex === entry.flatIndex
                 }">
              <div class="gi-accordion-header">
                <div class="gi-accordion-header__left">
                  <span class="gi-accordion-number">{{ entry.label }}</span>
                  <span class="gi-accordion-name">{{ entry.puzzle.puzzle_name }}</span>
                </div>
                <div class="gi-accordion-header__right">
                  <span v-if="pacingTypical(entry.puzzle)"
                        class="gi-accordion-badge gi-accordion-badge--pace-typical">
                    typically {{ pacingTypical(entry.puzzle) }}
                  </span>
                  <span class="gi-accordion-badge gi-accordion-badge--incomplete">Not completed</span>
                  <span v-if="parseInt(entry.puzzle.hints_given_count) > 0"
                        class="gi-accordion-badge gi-accordion-badge--hints">
                    {{ entry.puzzle.hints_given_count }} interaction{{ parseInt(entry.puzzle.hints_given_count) !== 1 ? 's' : '' }}
                  </span>
                  <span v-if="entry.puzzle.breakage_occurred"
                        class="gi-accordion-badge gi-accordion-badge--issues">
                    {{ typeof entry.puzzle.breakage_occurred === 'number' ? entry.puzzle.breakage_occurred : 1 }} issue{{ entry.puzzle.breakage_occurred > 1 ? 's' : '' }}
                  </span>
                  <div class="gi-accordion-actions">
                    <button v-if="editingPuzzleIndex !== entry.flatIndex && hasPermissions('game_history_update')"
                            class="gi-accordion-action-btn"
                            @click="editPuzzle(entry.flatIndex)">
                      <i class="tim-icons icon-pencil"></i>
                    </button>
                    <button v-if="editingPuzzleIndex === entry.flatIndex"
                            class="gi-accordion-action-btn gi-accordion-action-btn--save"
                            @click="savePuzzleInstance()">
                      <i class="tim-icons icon-check-2"></i> Save
                    </button>
                    <button v-if="editingPuzzleIndex === entry.flatIndex"
                            class="gi-accordion-action-btn"
                            @click="cancelEditingPuzzles()">
                      Cancel
                    </button>
                  </div>
                </div>
              </div>

              <!-- Notes -->
              <div v-if="editingPuzzleIndex !== entry.flatIndex && (entry.puzzle.hints_given_description || (entry.puzzle.breakage_occurred && entry.puzzle.breakage_description))"
                   class="gi-accordion-notes">
                <span v-if="entry.puzzle.hints_given_description" class="gi-timeline-card__note">
                  <i class="tim-icons icon-chat-33"></i> {{ entry.puzzle.hints_given_description }}
                </span>
                <span v-if="entry.puzzle.breakage_occurred && entry.puzzle.breakage_description" class="gi-timeline-card__note gi-timeline-card__note--issue">
                  <i class="tim-icons icon-alert-circle-exc"></i> {{ entry.puzzle.breakage_description }}
                </span>
              </div>

              <!-- Edit form -->
              <div v-if="editingPuzzleIndex === entry.flatIndex" class="gi-accordion-body">
                <div class="gi-accordion-edit-row">
                  <div>
                    <label>Completed</label>
                    <el-switch v-model="entry.puzzle.is_completed" :active-value="1" :inactive-value="0" active-color="#e14eca"></el-switch>
                  </div>
                </div>
                <div class="gi-accordion-edit-row" v-if="entry.puzzle.is_completed">
                  <div>
                    <label>Completion date</label>
                    <base-input :error="errs.solve_date">
                      <el-date-picker :editable="false" :format="day_of_week + date_format_cal"
                                      v-model="entry.puzzle.solve_time_d"
                                      placeholder="Date"
                                      :picker-options="{ firstDayOfWeek: first_day_of_week }">
                      </el-date-picker>
                    </base-input>
                  </div>
                  <div>
                    <label>Completion time</label>
                    <base-input :error="errs.solve_time">
                      <el-time-picker v-model="entry.puzzle.solve_time_t"
                                      :format="picker_time_format"
                                      placeholder="Time">
                      </el-time-picker>
                    </base-input>
                  </div>
                </div>
                <div class="gi-accordion-edit-row">
                  <div>
                    <label>Interaction count</label>
                    <base-input style="width: 150px">
                      <el-input-number size="mini" :min="0"
                                       v-model="entry.puzzle.hints_given_count">
                      </el-input-number>
                    </base-input>
                  </div>
                </div>
                <div class="gi-accordion-edit-row">
                  <div style="flex: 1">
                    <label>Interactions given</label>
                    <textarea class="form-control"
                              placeholder="List the interactions..."
                              v-model="entry.puzzle.hints_given_description"
                              rows="3"></textarea>
                  </div>
                </div>
                <div class="gi-accordion-edit-row">
                  <div style="flex: 1">
                    <label>Issues</label>
                    <textarea class="form-control"
                              placeholder="Describe the issues..."
                              v-model="entry.puzzle.breakage_description"
                              rows="3"></textarea>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </card>
      </div>

      <!-- Team & photos -->
      <div ref="photos" class="col-lg-12">
        <card card-body-classes="table-full-width">
          <h4 slot="header" class="card-title">Team and photos</h4>

          <div class="row">
            <!-- Photos column -->
            <div class="col-lg-5">
              <!-- Photos not enabled warning -->
              <div v-if="photos_not_enabled" style="margin-bottom: 8px">
                <base-button simple size="sm" type="danger" v-on:click="goToBilling()" v-if="hasPermissions('billing_view')">
                  <i class="tim-icons mr-1 icon-alert-circle-exc"></i> Go to billing
                </base-button>
                <p class="text-danger" style="font-size: 12px; margin: 0" v-else>
                  <i class="tim-icons mr-1 icon-alert-circle-exc"></i> Contact an administrator to activate photos.
                </p>
              </div>

              <div v-if="photos.length > 0">
                <!-- Enlarged preview -->
                <div class="gi-photo-preview" @click="modals.photo_lightbox = true" style="cursor: pointer">
                  <img :src="photo.overlay_url || photo.url" crossorigin="anonymous"
                       style="width: 100%; height: 340px; object-fit: cover; border-radius: 4px" />
                </div>

                <!-- Thumbnail grid with selection -->
                <div v-if="photos.length > 1" class="gi-photo-thumbs">
                  <div v-for="(item, index) in photos" :key="'PT' + index"
                       class="gi-photo-thumb"
                       :class="{'gi-photo-thumb--active': photo.id === item.id}"
                       @click="setActivePhoto(index)">
                    <img :src="item.overlay_url || item.url" crossorigin="anonymous" />
                    <div class="gi-photo-thumb__check" :class="{'gi-photo-thumb__check--selected': item.selected}" @click.stop="togglePhotoSelection(index)">
                      <i v-if="item.selected" class="tim-icons icon-check-2"></i>
                    </div>
                  </div>
                </div>

                <!-- Status + actions -->
                <div style="margin-top: 8px; display: flex; align-items: center; gap: 6px; flex-wrap: wrap">
                  <span class="gi-badge gi-badge--muted">{{selectedPhotos.length}} of {{photos.length}} selected</span>
                  <base-button v-if="email_active == 1 && selectedPhotos.length > 0"
                               :disabled="photos_not_enabled || emailing_all_photos"
                               size="sm" simple type="primary"
                               style="padding: 4px 8px; font-size: 11px"
                               v-on:click="emailSelectedPhotos()">
                    <i class="tim-icons icon-email-85 mr-1"></i>
                    <span v-if="emailing_all_photos">Sending...</span>
                    <span v-else>Email {{selectedPhotos.length}} {{selectedPhotos.length === 1 ? 'photo' : 'photos'}} to team</span>
                  </base-button>
                  <el-tooltip content="Upload photo" effect="light" :open-delay="300" placement="top">
                    <button :disabled="photos_not_enabled || !gameInstance.booking_slot_id" class="gi-accordion-action-btn gi-accordion-action-btn--save"
                      @click="modals.upload_photo = true">
                      <i class="tim-icons icon-simple-add"></i>
                    </button>
                  </el-tooltip>
                  <el-tooltip content="Remove photo" effect="light" :open-delay="300" placement="top">
                    <button v-if="photo.id != ''" :disabled="photos_not_enabled" class="gi-accordion-action-btn"
                      @click="deletePhoto()" style="border-color: #fd5d93; color: #fd5d93">
                      <i class="tim-icons icon-trash-simple"></i>
                    </button>
                  </el-tooltip>
                </div>
                <p v-if="selectedPhotos.length > 10" style="font-size: 11px; color: #fd5d93; margin: 4px 0 0">
                  <i class="tim-icons icon-alert-circle-exc mr-1"></i>Large attachments may not deliver to all email providers
                </p>
              </div>
              <div v-else class="gi-empty" style="padding: 16px">
                <div class="gi-empty__icon" style="width: 40px; height: 40px; margin-bottom: 10px">
                  <i class="tim-icons icon-camera-18" style="font-size: 16px"></i>
                </div>
                <p class="gi-empty__subtitle">No photos assigned</p>
                <button :disabled="photos_not_enabled || !gameInstance.booking_slot_id" class="gi-accordion-action-btn gi-accordion-action-btn--save"
                  style="margin-top: 8px" @click="modals.upload_photo = true">
                  <i class="tim-icons icon-simple-add"></i> Upload photo
                </button>
              </div>
            </div>

            <!-- Team column -->
            <div class="col-lg-7">
              <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px; flex-wrap: wrap">
                <span class="gi-section__label gi-section__label--lg">
                  <i class="tim-icons icon-single-02"></i> Team
                </span>
                <button v-show="hasPermissions('game_history_update')" class="gi-accordion-action-btn"
                  @click="addPlayer()">
                  <i class="tim-icons icon-simple-add"></i> Add participant
                </button>
                <button v-if="team.length > 0 && !team[0].name && !creating_team_name" class="gi-accordion-action-btn" @click="createTeamName()">
                  <i class="tim-icons icon-simple-add"></i> Set team name
                </button>
              </div>

              <!-- Team name -->
              <div class="gi-details" style="margin-bottom: 8px">
                <div v-if="team.length > 0 && team[0].name && !creating_team_name && !editing_team_name" style="margin-bottom: 14px">
                  <label class="gi-team-label">Team name</label>
                  <h4 class="gi-team-value">
                    {{team[0].name}}
                    <button class="gi-accordion-action-btn" style="height: 20px; padding: 0 4px; border: none; background: none" @click="editTeamName()">
                      <i class="tim-icons icon-pencil" style="font-size: 10px"></i>
                    </button>
                  </h4>
                </div>
                <div v-if="editing_team_name" style="padding: 7px 0">
                  <base-input label="Team name" style="margin-bottom: 0; width: 220px" v-model="team[0].name" placeholder="Team name"></base-input>
                  <div style="display: flex; align-items: center; gap: 6px; margin-top: 6px">
                    <base-button @click.native="confirmTeamName()" type="success" simple size="sm" style="padding: 4px 10px; font-size: 11px">Save</base-button>
                    <base-button @click.native="editing_team_name = false" type="default" simple size="sm" style="padding: 4px 10px; font-size: 11px">Cancel</base-button>
                  </div>
                </div>
                <div v-if="creating_team_name" style="padding: 7px 0">
                  <base-input label="Team name" style="margin-bottom: 0; width: 220px" v-model="team[0].name" placeholder="Team name"></base-input>
                  <div style="display: flex; align-items: center; gap: 6px; margin-top: 6px">
                    <base-button @click.native="confirmTeamNameCreate()" type="success" simple size="sm" style="padding: 4px 10px; font-size: 11px">Save</base-button>
                    <base-button @click.native="creating_team_name = false" type="default" simple size="sm" style="padding: 4px 10px; font-size: 11px">Cancel</base-button>
                  </div>
                </div>
              </div>

              <!-- Player list -->
              <div class="gi-player-list">
                <template v-for="row in team">
                  <div class="gi-player" :class="{'gi-player--minor': row.is_minor == 1}" :key="'TP' + row.user_id">
                    <div class="gi-player__avatar">{{ row.first_name ? row.first_name.charAt(0).toUpperCase() : '?' }}{{ row.last_name ? row.last_name.charAt(0).toUpperCase() : '' }}</div>
                    <div>
                      <div class="gi-player__name">
                        {{row.first_name}} {{row.last_name}}
                        <span v-if="row.opted_out_photo_sharing" class="gi-badge gi-badge--red" style="font-size: 9px; padding: 1px 5px; margin-left: 4px">Opted out</span>
                      </div>
                      <div v-if="row.is_minor == 0" class="gi-player__email">{{row.email}}</div>
                      <span v-if="row.is_minor == 1" class="gi-badge gi-badge--blue" style="font-size: 9px; padding: 1px 5px; margin-top: 2px">Minor</span>
                    </div>
                    <div class="gi-player__actions">
                      <el-tooltip v-if="hasPermissions('customer_view') && row.is_minor == 0" content="View customer" effect="light" :open-delay="300" placement="top">
                        <a target="_blank" :href="'/customers/details?id=' + row.user_id">
                          <i class="tim-icons el-icon-top-right"></i>
                        </a>
                      </el-tooltip>
                      <el-tooltip v-if="row.is_minor == 0 && photos.length > 0 && email_active == 1" content="Email photo" effect="light" :open-delay="300" placement="top">
                        <button :disabled="photos_not_enabled" class="gi-accordion-action-btn" style="height: 20px; padding: 0 4px; border: none; background: none"
                          @click="emailPhoto(row)">
                          <i class="tim-icons icon-email-85"></i>
                        </button>
                      </el-tooltip>
                    </div>
                  </div>
                </template>
              </div>

              <!-- Non-participating guardians -->
              <div v-if="non_participants.length > 0" style="margin-top: 12px">
                <div class="gi-divider"></div>
                <span class="gi-accordion-field__label" style="margin-bottom: 6px; display: block">Non-participating guardians</span>
                <div class="gi-player-list">
                  <template v-for="row in non_participants">
                    <div class="gi-player" :class="{'gi-player--minor': row.is_minor == 1}" :key="'NP' + row.user_id">
                      <div class="gi-player__avatar">{{ row.first_name ? row.first_name.charAt(0).toUpperCase() : '?' }}{{ row.last_name ? row.last_name.charAt(0).toUpperCase() : '' }}</div>
                      <div>
                        <div class="gi-player__name">
                          {{row.first_name}} {{row.last_name}}
                          <span v-if="row.opted_out_photo_sharing" class="gi-badge gi-badge--red" style="font-size: 9px; padding: 1px 5px; margin-left: 4px">Opted out</span>
                        </div>
                        <div v-if="row.is_minor == 0" class="gi-player__email">{{row.email}}</div>
                        <span v-if="row.is_minor == 1" class="gi-badge gi-badge--blue" style="font-size: 9px; padding: 1px 5px; margin-top: 2px">Minor</span>
                      </div>
                      <div v-if="row.is_minor == 0 && email_active == 1" class="gi-player__actions">
                        <el-tooltip content="View customer" effect="light" :open-delay="300" placement="top">
                          <button class="gi-accordion-action-btn" style="height: 20px; padding: 0 4px; border: none; background: none"
                            @click="viewCustomerDetails(row)">
                            <i class="tim-icons icon-badge"></i>
                          </button>
                        </el-tooltip>
                        <el-tooltip v-if="photos.length > 0" content="Email photo" effect="light" :open-delay="300" placement="top">
                          <button :disabled="photos_not_enabled" class="gi-accordion-action-btn" style="height: 20px; padding: 0 4px; border: none; background: none"
                            @click="emailPhoto(row)">
                            <i class="tim-icons icon-email-85"></i>
                          </button>
                        </el-tooltip>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </card>
      </div>

      <!-- Unlinked interactions (not tied to a specific objective) -->
      <div class="col-lg-12">
        <card v-if="getUnlinkedInteractions().length > 0" card-body-classes="table-full-width">
          <h4 slot="header" class="card-title">General interactions
            <badge type="primary" class="ml-2">{{ getUnlinkedInteractions().length }}</badge>
          </h4>
          <div class="gi-interaction-list" style="padding: 0 16px 16px">
            <div v-for="(log, li) in getUnlinkedInteractions()"
                 :key="'UL' + li"
                 class="gi-interaction-row">
              <span class="gi-interaction-row__content">{{ getClueLabel(log) }}</span>
              <span class="gi-interaction-row__meta">
                <span class="gi-hint-type-badge" :class="'gi-hint-type-badge--' + log.type">{{ log.type }}</span>
                <span class="gi-interaction-row__time">{{ getLocalDate(log.timestamp) }} {{ getLocalTime(log.timestamp) }}</span>
              </span>
            </div>
          </div>
        </card>
      </div>
    </div>
    <!-- Photo lightbox -->
    <modal :show.sync="modals.photo_lightbox"
           body-classes="p-0"
           modal-classes="modal-dialog-centered modal-lg">
      <card type="secondary"
            body-classes="p-0"
            class="border-0 mb-0">
        <div class="gi-lightbox">
          <!-- Image with fade transition -->
          <transition name="gi-lightbox-fade" mode="out-in">
            <img :key="photo.id"
                 :src="photo.overlay_url || photo.url"
                 crossorigin="anonymous"
                 class="gi-lightbox__img"
                 @load="lightboxLoading = false" />
          </transition>
          <div v-if="lightboxLoading" class="gi-lightbox__loader">
            <svg class="custom-spinner" viewBox="0 0 50 50" style="width: 36px; height: 36px;">
              <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5" />
            </svg>
          </div>

          <!-- Overlay nav buttons -->
          <template v-if="photos.length > 1">
            <button class="gi-lightbox__nav gi-lightbox__nav--prev" @click="prevPhoto()">
              <i class="tim-icons icon-minimal-left"></i>
            </button>
            <button class="gi-lightbox__nav gi-lightbox__nav--next" @click="nextPhoto()">
              <i class="tim-icons icon-minimal-right"></i>
            </button>
          </template>

          <!-- Bottom bar -->
          <div class="gi-lightbox__bar">
            <span class="gi-lightbox__counter">{{activePhotoIndex + 1}} / {{photos.length}}</span>
            <button class="gi-lightbox__close" @click="modals.photo_lightbox = false">
              <i class="tim-icons icon-simple-remove mr-1"></i> Close
            </button>
          </div>
        </div>
      </card>
    </modal>

    <modal :show.sync="modals.upload_photo"
           body-classes="p-0"
           modal-classes="modal-dialog-centered modal-sm">
      <card type="secondary"
            header-classes="bg-white pb-5"
            body-classes="px-lg-5 py-lg-5"
            class="border-0 mb-0">
        <div class="text-center">
          <div
            class="fileinput-new thumbnail">
            <img :src="uploaded_image" alt="preview" />

          </div>
          <span class="btn btn-primary btn-sm btn-simple btn-file mt-3">New photo
                <input type="file"
                       accept="image/*"
                       @change="handleUpload"

                />
          </span>
        </div>

        <div class="row d-none d-sm-block text-center mt-3">
          <base-button type="secondary" @click="modals.upload_photo = false">Cancel</base-button>
          <base-button class="ml-3" v-on:click="savePhoto()" type="primary" >Save</base-button>
        </div>
        <div class="row text-center d-block d-sm-none mt-3">
          <base-button type="secondary" simple size="sm" @click="modals.upload_photo = false">Cancel</base-button>
          <base-button class="ml-3" simple size="sm" v-on:click="savePhoto()" type="primary" >Save</base-button>
        </div>
      </card>
    </modal>

    <!--    Add player Modal-->
    <modal :show.sync="modals.add_player"
           body-classes="p-0"
           modal-classes="modal-dialog-centered modal-lg">
      <card type="secondary"
            header-classes="bg-white pb-5"
            body-classes="px-lg-5 py-lg-5"
            class="border-0 mb-0">

        <p class="text-white">Add participant to event</p>
        <base-alert type="warning"><i class="tim-icons icon-alert-circle-exc"></i>
          This will create a waiver record without customer's signature</base-alert>
        <div class="row">
          <div class="col-6">
            <base-input label="First name" v-model="user.first_name" placeholder="First name"></base-input>
            <p v-show="errors.first_name" class="text-danger small">First name cannot be empty</p>
          </div>
          <div class="col-6">
            <base-input label="Last name" v-model="user.last_name" placeholder="Last name"></base-input>
            <p v-show="errors.last_name" class="ml-1 text-danger small">Last name cannot be empty</p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <base-input label="Email" v-model="user.email" placeholder="Email"></base-input>
            <p v-show="errors.email" class="ml-1 text-danger small">Please provide a valid email</p>
          </div>
          <div class="col-6">
            <base-input label="Phone (Optional)" v-mask="'(###) ###-####'" v-model="user.phone" placeholder="(999) 999-9999"></base-input>
          </div>
        </div>
        <div class="row d-none d-sm-block text-center mt-3">
          <base-button type="secondary" @click="modals.add_player = false">Cancel</base-button>
          <base-button class="ml-3" type="primary" @click="addPlayerConfirm()">Save</base-button>
        </div>
        <div class="row d-block d-sm-none text-center mt-3">
          <base-button simple size="sm" type="secondary" @click="modals.add_player = false">Cancel</base-button>
          <base-button simple size="sm" class="ml-3" type="primary" @click="addPlayerConfirm()">Save</base-button>
        </div>
      </card>
    </modal>
  </div>
</template>

<script>
  import { BFormInput, BTable, BPagination } from 'bootstrap-vue'
  import { TimeLine, TimeLineItem, TabPane, Tabs, Collapse, CollapseItem, BaseAlert, Badge, Modal } from 'src/components';
  import {Select, Option, Tooltip, TimePicker, CarouselItem, Carousel, Switch, Dropdown, DropdownMenu, DropdownItem} from 'element-ui';
  import { tagChipStyle } from '@/util/tagChip';
  import { Auth } from 'aws-amplify'
  import { API_LOCATION } from '../../../api-config'
  const axios = require('axios')
    import {date, dayOfWeek, formattedDate, time} from "@/plugins/dateFormatter";
  import {formatGroupDate, formatGroupTime} from "@/plugins/dates";
  import {groupTimeDigits, formatCustomTime, toStoredCustomTime} from "@/util/customTime";
  import Breadcrumb from "@/components/Breadcrumb/Breadcrumb";
  import BreadcrumbItem from "@/components/Breadcrumb/BreadcrumbItem";
  import { buildBaseline, summarizeHistoryInstance, formatDelta, formatDuration, statusLabel } from "@/util/trackerPacing";
  import _ from "lodash";
  import moment from "moment";

  const NO_RECIPIENTS_MESSAGE = 'Photos are emailed to the players on this booking who signed a waiver with an email address, and none of them has one. Add an email to a player, or assign the photo to a customer on the Photos page and send it from there.'
// True when the photo email endpoint rejected the send because nobody on the
// booking has an email address (400 with error 'No valid recipients').
function isNoRecipientsError(err) {
  if (!err || !err.response || err.response.status !== 400) return false
  let data = err.response.data
  if (typeof data === 'string') { try { data = JSON.parse(data) } catch (e) { return false } }
  return !!(data && data.error === 'No valid recipients')
}

export default {
    name: "GameInstance",
    components: {
      BreadcrumbItem,
      Breadcrumb,
      BFormInput,
      BTable,
      BPagination,
      TimeLine,
      TimeLineItem,
      [CarouselItem.name]: CarouselItem,
      [Carousel.name]: Carousel,
      [Select.name]: Select,
      [Option.name]: Option,
      [Dropdown.name]: Dropdown,
      [DropdownMenu.name]: DropdownMenu,
      [DropdownItem.name]: DropdownItem,
      TabPane,
      Tabs,
      Collapse,
      CollapseItem,
      BaseAlert,
      Badge,
      Modal,
      Tooltip,
      [TimePicker.name]: TimePicker,
      [Switch.name]: Switch
    },
    data() {
      return {
      alertDialog: { show: false, title: '', message: '', variant: 'warning', showCancel: false, confirmText: 'OK', cancelText: 'Cancel', confirmVariant: 'primary' },
      alertResolve: null,
        pacingRows: null,
        optional_fields: [],
        photos: [],
        loading: false,
        bookingTags: [],
        groupTags: [],
        savingTags: false,
        tagsLoading: false,
        time_format: JSON.parse(localStorage.getItem('group')).time_format,
        date_format: JSON.parse(localStorage.getItem('group')).date_format,
        first_day_of_week: JSON.parse(localStorage.getItem('group')).first_day_of_week,
        date_format_cal: '',
        day_of_week: '',
        picker_time_format: 'hh:mm A',
        selectedGameMaster: '',
        company_users: [],
        editing_team_name: false,
        creating_team_name: false,
        uploaded_image: 'https://console.offthecouch.io/img/placeholder.jpg',
        modals: {
          upload_photo: false,
          send_photo: false,
          add_player: false,
          photo_lightbox: false
        },
        editingPuzzleIndex: null,
        puzzleBackup: null,
        editingGame: false,
        customTimeInput: '',
        user: {
          first_name: '',
          last_name: '',
          email: '',
          phone: ''
        },
        editing: {
          booking_time: '',
          start_time: '',
          end_time: '',
          start_date: '',
          end_date: ''
        },
        perPage: 10,
        currentPage: 1,
        totalRows: 1,
        filter: null,
        email_active: 0,
        teamFields: ['players'],
        errors: {
          first_name: false,
          last_name: false,
          email: false
        },
        errs: {
          start_time: '',
          end_time: '',
          booking_time: '',
          solve_date: '',
          solve_time: '',
        },
        gameInstance: {
          booking_date: '',
          booking_start_time: '',
          start_time: '2022-06-19T14:36:00.000Z'
        },
        photo: {
          url: 'https://console.offthecouch.io/img/placeholder.jpg',
          id: ''
        },
        puzzleInstances: [],
        team: [],
        non_participants: [],
        exceed_time: false,
        layout: 'landscape',
        photos_not_enabled: false,
        emailing_all_photos: false,
        lightboxLoading: false,
        interactionLog: []
      };
    },
    computed: {
      availableTags() {
        const applied = new Set(this.bookingTags.map(t => t.id))
        return this.groupTags.filter(t => !applied.has(t.id))
      },
      selectedPhotos() {
        return this.photos.filter(p => p.selected)
      },
      activePhotoIndex() {
        return this.photos.findIndex(p => p.id === this.photo.id)
      },
      displayPuzzles() {
        const result = []
        const topLevel = this.puzzleInstances
          .filter(p => !p.parent_id)
          .sort((a, b) => (a.position || 0) - (b.position || 0))

        topLevel.forEach((parent, pi) => {
          const flatIndex = this.puzzleInstances.indexOf(parent)
          const displayNum = pi + 1
          result.push({ puzzle: parent, flatIndex, label: String(displayNum), isChild: false })

          const children = this.puzzleInstances
            .filter(c => c.parent_id && Number(c.parent_id) === Number(parent.puzzle_id))
            .sort((a, b) => (a.position || a.id || 0) - (b.position || b.id || 0))
          children.forEach((child, ci) => {
            const childFlatIndex = this.puzzleInstances.indexOf(child)
            result.push({
              puzzle: child,
              flatIndex: childFlatIndex,
              label: displayNum + String.fromCharCode(97 + ci),
              isChild: true
            })
          })
        })
        return result
      },
      allCompletedPuzzles() {
        return this.displayPuzzles
          .filter(e => {
            if (e.flatIndex === this.editingPuzzleIndex && this.puzzleBackup) {
              return this.puzzleBackup.is_completed == 1
            }
            return e.puzzle.is_completed == 1
          })
          .sort((a, b) => {
            const aSolve = (a.flatIndex === this.editingPuzzleIndex && this.puzzleBackup)
              ? this.puzzleBackup.solve_time : a.puzzle.solve_time
            const bSolve = (b.flatIndex === this.editingPuzzleIndex && this.puzzleBackup)
              ? this.puzzleBackup.solve_time : b.puzzle.solve_time
            const aTime = aSolve ? new Date(aSolve).getTime() : 0
            const bTime = bSolve ? new Date(bSolve).getTime() : 0
            return aTime - bTime
          })
      },
      endTimeMs() {
        if (!this.gameInstance.end_time) return null
        return new Date(this.gameInstance.end_time).getTime()
      },
      completedBeforeEnd() {
        if (!this.endTimeMs) return this.allCompletedPuzzles
        return this.allCompletedPuzzles.filter(e => {
          const solve = (e.flatIndex === this.editingPuzzleIndex && this.puzzleBackup)
            ? this.puzzleBackup.solve_time : e.puzzle.solve_time
          return !solve || new Date(solve).getTime() <= this.endTimeMs
        })
      },
      pacingSummary() {
        if (!Array.isArray(this.pacingRows) || this.pacingRows.length === 0 || !this.gameInstance) {
          return null
        }
        const baseline = buildBaseline(this.pacingRows)
        if (!baseline.enoughHistory) {
          return null
        }
        return summarizeHistoryInstance({
          baseline,
          rows: this.puzzleInstances,
          totalSeconds: this.gameInstance.total_seconds,
          limitSeconds: (Number(this.gameInstance.duration_minutes) || 0) * 60
        })
      },
      pacingStatusLabel() {
        return this.pacingSummary ? (statusLabel(this.pacingSummary.status) || 'Unknown') : ''
      },
      completedAfterEnd() {
        if (!this.endTimeMs) return []
        return this.allCompletedPuzzles.filter(e => {
          const solve = (e.flatIndex === this.editingPuzzleIndex && this.puzzleBackup)
            ? this.puzzleBackup.solve_time : e.puzzle.solve_time
          return solve && new Date(solve).getTime() > this.endTimeMs
        })
      },
      incompletePuzzles() {
        return this.displayPuzzles.filter(e => {
          if (e.flatIndex === this.editingPuzzleIndex && this.puzzleBackup) {
            return this.puzzleBackup.is_completed != 1
          }
          return !e.puzzle.is_completed || e.puzzle.is_completed == 0
        })
      }
    },
    methods: {
      tagChipStyle,
      tagsBaseUrl() {
        let companyId = localStorage.getItem('companyId')
        let companyGroupId = JSON.parse(localStorage.getItem('group')).id
        return API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId
      },
      async loadBookingTags() {
        this.bookingTags = []
        if (!this.gameInstance || !this.gameInstance.booking_slot_id) return
        this.tagsLoading = true
        try {
          let token = await this.getAuthToken()
          let config = { headers: { Authorization: token } }
          let catalog = await axios.get(this.tagsBaseUrl() + '/tags', config)
          this.groupTags = catalog.data || []
          let applied = await axios.get(this.tagsBaseUrl() + '/bookings/' + this.gameInstance.booking_slot_id + '/tags', config)
          this.bookingTags = applied.data || []
        } catch (e) {
          console.log('Tags not available:', e.message)
        } finally {
          this.tagsLoading = false
        }
      },
      async saveTagSet(ids) {
        if (this.savingTags || !this.gameInstance.booking_slot_id) return
        this.savingTags = true
        let previous = this.bookingTags
        try {
          let token = await this.getAuthToken()
          let config = { headers: { Authorization: token } }
          let url = this.tagsBaseUrl() + '/bookings/' + this.gameInstance.booking_slot_id + '/tags'
          let response = await axios.put(url, { tag_ids: ids }, config)
          this.bookingTags = response.data.tags || []
        } catch (e) {
          this.bookingTags = previous
          this.$notify({ type: 'danger', message: 'Failed to update tags', timeout: 3000 })
        } finally {
          this.savingTags = false
        }
      },
      addTag(tagId) {
        if (this.bookingTags.some(t => t.id === tagId)) return
        this.saveTagSet([...this.bookingTags.map(t => t.id), tagId])
      },
      removeTag(tag) {
        this.saveTagSet(this.bookingTags.map(t => t.id).filter(id => id !== tag.id))
      },
      formatCustomTime,
      onCustomTimeInput(val){
        // gameInstance.custom_time stays canonical HH:MM:SS; the field shows the grouped form
        this.customTimeInput = groupTimeDigits(val)
        this.gameInstance.custom_time = toStoredCustomTime(this.customTimeInput)
      },
      onCustomTimeBlur(){
        this.customTimeInput = formatCustomTime(this.gameInstance.custom_time)
      },
      getInteractionsForPuzzle(puzzleId) {
        return this.interactionLog.filter(i => i.puzzle_id && Number(i.puzzle_id) === Number(puzzleId))
      },
      getUnlinkedInteractions() {
        return this.interactionLog.filter(i => !i.puzzle_id)
      },
      hasFieldEnabled(fieldId){
        for(let n in this.optional_fields){
          if(this.optional_fields[n].field_id === fieldId && this.optional_fields[n].is_enabled){
            return true
          }
        }
        return false
      },
      goToBilling(){
        this.$router.push('/settings/billing')
      },
      hasPermissions(scope){

        let permissions = JSON.parse(localStorage.getItem('permissions'))
        const matchFound = permissions.some(element => element === scope);
        return matchFound
      },
      validateTime(time, day){
        let today = new Date(day)
        time.setMonth(today.getMonth(), today.getDate())
      },
      setActivePhoto(index){
        this.photo = this.photos[index]
      },
      togglePhotoSelection(index){
        this.$set(this.photos[index], 'selected', !this.photos[index].selected)
      },
      prevPhoto(){
        this.lightboxLoading = true
        let index = this.activePhotoIndex > 0 ? this.activePhotoIndex - 1 : this.photos.length - 1
        this.setActivePhoto(index)
      },
      nextPhoto(){
        this.lightboxLoading = true
        let index = this.activePhotoIndex < this.photos.length - 1 ? this.activePhotoIndex + 1 : 0
        this.setActivePhoto(index)
      },
      getInitials(first, last) {
        return ((first || '').charAt(0) + (last || '').charAt(0)).toUpperCase()
      },
      addPlayer(){
        this.modals.add_player = true
      },
      viewCustomerDetails(user){
        this.$router.push({ path: '/customers/details', query: { id:  user.user_id }})
      },
      async confirmTeamName(){
        this.editing_team_name = false
        this.loading = true
        var token = await this.getAuthToken()
        let config = { headers: { Authorization: token } }
        let companyId = localStorage.getItem('companyId')
        let url = API_LOCATION + 'companies/' + companyId + '/teams/' + this.team[0].team_id

        let data = {
          team_name: this.team[0].name
        }

        axios.put(url, data, config)
          .then(response => {
            this.loading = false
          })
          .catch(error => {
            this.loading = false
            console.log(error)
            if(error.response.status == 401){
              this.$router.push('/forbidden')
            }
          })

      },
      async addPlayerConfirm(){
        this.modals.add_player = false
        this.loading = true
        var token = await this.getAuthToken()
        let config = { headers: { Authorization: token } }
        let companyId = localStorage.getItem('companyId')
        let url = API_LOCATION + 'companies/' + companyId + '/games/instances/' + this.gameInstance.id + '/waiver'

        let data = {
          user: this.user,
          game_id: this.gameInstance.game_id,
          booking_slot_id: this.gameInstance.booking_slot_id,
          timestamp: new Date(),
          team_id: null
        }
        if(this.team.length > 0){
          data.team_id = this.team[0].team_id
        }
        axios.post(url, data, config)
          .then(response => {
            this.loading = false
            this.users = {
              first_name: '',
              last_name: '',
              email: '',
              phone: ''
            }
            this.getGameData()
          })
          .catch(error => {
            this.loading = false
            console.log(error)
            if(error.response.status == 401){
              this.$router.push('/forbidden')
            }
          })

      },
      async confirmTeamNameCreate(){
        this.creating_team_name = false
        this.loading = true
        var token = await this.getAuthToken()
        let config = { headers: { Authorization: token } }
        let companyId = localStorage.getItem('companyId')
        let url = API_LOCATION + 'companies/' + companyId + '/teams'

        let data = {}

        if(this.team[0].team_id){
          data = {
            team_name: this.team[0].name,
          }
          url = API_LOCATION + 'companies/' + companyId + '/teams/' + this.team[0].team_id
          axios.put(url, data, config)
            .then(response => {
              this.loading = false
              this.getGameData()
            })
            .catch(error => {
              this.loading = false
              console.log(error)
              if(error.response.status == 401){
                this.$router.push('/forbidden')
              }
            })
        } else {
          let data = {
            name: this.team[0].name,
            users: this.team
          }
          axios.post(url, data, config)
            .then(response => {
              this.loading = false
              this.getGameData()
            })
            .catch(error => {
              this.loading = false
              console.log(error)
              if(error.response.status == 401){
                this.$router.push('/forbidden')
              }
            })
        }

      },
      createTeamName(){
        this.creating_team_name = true
      },
      editTeamName(){
        this.editing_team_name = true
      },
      getAuthToken() {
        return new Promise ( function(resolve) {
          Auth.currentSession()
            .then(data =>{
              resolve(data.idToken.jwtToken)
            })
            .catch(err => console.log(err))
        })
      },
      inviteUser() {
        this.$router.push('/settings/user-invite')
      },
      onFiltered(filteredItems) {
        // Trigger pagination to update the number of buttons/pages due to filtering
        this.totalRows = filteredItems.length
        this.currentPage = 1
      },
      cancelEditingGame(){
        this.editingGame = false
      },
      cancelEditingPuzzles(){
        if(this.puzzleBackup && this.editingPuzzleIndex !== null){
          this.$set(this.puzzleInstances, this.editingPuzzleIndex, this.puzzleBackup)
        }
        this.puzzleBackup = null
        this.editingPuzzleIndex = null
        this.errs.solve_date = ''
        this.errs.solve_time = ''
      },
      getDayOfWeek(d){
        if(JSON.parse(localStorage.getItem('group')).show_day_of_week){
          return dayOfWeek(d) + ', '
        }
        return ''
      },
      editGameInstance() {
        let dt = new Date()
        if(this.gameInstance.start_time){
          dt.setHours(this.gameInstance.booking_start_time.split(':')[0])
          dt.setMinutes(this.gameInstance.booking_start_time.split(':')[1])
        }

        this.editing.booking_time = dt

        let dtSt = new Date()
        if(this.gameInstance.start_time  && !this.gameInstance.start_time.includes('0000')){
          dtSt.setFullYear(this.gameInstance.start_time.split('T')[0].split('-')[0])
          dtSt.setMonth(this.gameInstance.start_time.split('T')[0].split('-')[1] - 1, this.gameInstance.start_time.split('T')[0].split('-')[2])
          dtSt.setHours(this.gameInstance.start_time.split('T')[1].split(':')[0])
          dtSt.setMinutes(this.gameInstance.start_time.split('T')[1].split(':')[1])
        }

        this.editing.start_time = dtSt
        this.editing.start_date = dtSt

        let dtEt = new Date()
        if(this.gameInstance.end_time && !this.gameInstance.end_time.includes('0000')){
          dtEt.setFullYear(this.gameInstance.end_time.split('T')[0].split('-')[0])
          dtEt.setMonth(this.gameInstance.end_time.split('T')[0].split('-')[1] - 1, this.gameInstance.end_time.split('T')[0].split('-')[2])
          dtEt.setHours(this.gameInstance.end_time.split('T')[1].split(':')[0])
          dtEt.setMinutes(this.gameInstance.end_time.split('T')[1].split(':')[1])
        }

        this.editing.end_time = dtEt
        this.editing.end_date = dtEt

        this.customTimeInput = formatCustomTime(this.gameInstance.custom_time)
        this.editingGame = true
      },
      editPuzzle(flatIndex){
        let puzzle = this.puzzleInstances[flatIndex]
        this.puzzleBackup = JSON.parse(JSON.stringify(puzzle))
        if(puzzle.solve_time && typeof puzzle.solve_time === 'string' && puzzle.solve_time.includes('T')){
          let dt = new Date()
          dt.setFullYear(puzzle.solve_time.split('T')[0].split('-')[0])
          dt.setMonth(puzzle.solve_time.split('T')[0].split('-')[1] - 1, puzzle.solve_time.split('T')[0].split('-')[2])
          dt.setHours(puzzle.solve_time.split('T')[1].split(':')[0])
          dt.setMinutes(puzzle.solve_time.split('T')[1].split(':')[1])
          puzzle.solve_time = dt
        }
        this.editingPuzzleIndex = flatIndex
      },
      async delete() {
        this.loading = true
        var token = await this.getAuthToken()
        let config = { headers: { Authorization: token } }
        let companyId = localStorage.getItem('companyId')
        let gameInstanceId = this.$route.query.id
        let url = API_LOCATION + 'companies/' + companyId + '/games/instances/' + gameInstanceId

        axios.delete(url, config)
          .then(response => {
            this.loading = false
            this.$router.push('/games/history?count=10&page=1')
          })
          .catch(error => {
            this.loading = false
            console.log(error)
            if(error.response.status == 401){
              this.$router.push('/forbidden')
            }
          })
      },
      async deletePhoto(){
        this.loading = true
        let token = await this.getAuthToken()
        let config = {headers: {Authorization: token}}
        let companyId = localStorage.getItem('companyId')
        let companyGroupId = JSON.parse(localStorage.getItem('group')).id
        let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/photos/' + this.photo.id + '/assign?link_id=' + this.photo.link_id + '&link_type=' + 1

        axios.delete(url, config)
          .then(response => {
            this.loading = false
            this.showAlert({
              title: 'Photo has been unassigned',
              variant: 'success',
              confirmText: 'Ok'
            })
            this.getGameData()
          }).catch((err) => {
          console.log(err)
          this.loading = false
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert({
              title: 'Something went wrong!',
              variant: 'danger',
              confirmText: 'Try again'
            })
          }
        })
      },
      deleteGameInstance(){
        this.showAlert({
          title: 'Are you sure?',
          message: `The event record will be permanently deleted`,
          variant: 'warning',
          showCancel: true,
          confirmText: 'Yes, delete it!'
        }).then((response)=> {
          if(response === true){
            this.delete();
          }
        })

      },
      async getGameData(){
        this.loading = true
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }
        const companyId = localStorage.getItem('companyId')
        const gameInstanceId =  this.$route.query.id
        const companyGroupId = JSON.parse(localStorage.getItem('group')).id
        let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/game-instances/' + gameInstanceId

        axios.get(url, config)
          .then(response => {
            this.loading = false
            this.gameInstance = response.data.gameInstance
            this.loadBookingTags()
            this.gameInstance.completed = Boolean(this.gameInstance.completed)
            this.gameInstance.is_corporate_booking = Boolean(this.gameInstance.is_corporate_booking)
            this.puzzleInstances = response.data.puzzleInstances
            this.pacingRows = response.data.pacing && Array.isArray(response.data.pacing.rows) ? response.data.pacing.rows : null
            this.team = response.data.team
            this.non_participants = response.data.non_participants
            this.email_active = response.data.email_active
            this.optional_fields = response.data.fields
            this.photos = response.data.photo.map(p => ({ ...p, selected: true }))
            this.selectedGameMaster = {
              id: response.data.gameInstance.gm_id,
              first_name: response.data.gameInstance.first_name,
              last_name: response.data.gameInstance.last_name,
              email: response.data.gameInstance.email
            }
            if(response.data.photo.length > 0){
              this.photo = response.data.photo[0]
            }

            for (let n in this.puzzleInstances){
              if(this.puzzleInstances[n].is_completed === 0){
                this.puzzleInstances[n].solve_time = null
                this.puzzleInstances[n].solve_time_d = null
                this.puzzleInstances[n].solve_time_t = null
              } else {
                this.puzzleInstances[n].solve_time_d = moment(this.puzzleInstances[n].solve_time_d, "YYYY-MM-DD")
                this.puzzleInstances[n].solve_time_t = moment(this.puzzleInstances[n].solve_time_t, "HH:mm:ss")
              }
            }

            this.interactionLog = response.data.interactionLog || []
            this.getCompanyUsers()
          })
          .catch(err => {
            this.loading = false
            console.log(err)
            if(err.response.status == 401){
              this.showAlert({
                title: 'Error',
                message: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
                variant: 'danger'
              }).then((response)=> {
                this.$router.push('/')
              })
            }
            else if(err.response.status == 500){
              this.showAlert({
                title: 'Error',
                message: 'The feature has not been enabled as part of your subscription plan.',
                variant: 'danger'
              }).then((response)=> {
                this.$router.push('/')
              })
            }
            else {
              this.showAlert({ title: 'Error', message: 'Something went wrong', variant: 'danger' })
            }
          })

      },
      getMinutesRemaining(g){

        let total_time = parseInt(this.getTotalTime(this.puzzleInstances))
        let game_duration = parseInt(g.duration_minutes)

        if(isNaN(total_time) || isNaN(game_duration)){
          this.exceed_time = false
          return '-'
        }

        if(parseInt(game_duration - total_time) >= 0){
          this.exceed_time = false
          return parseInt(game_duration - total_time) + ' min'
        } else {
          this.exceed_time = true
          return parseInt(total_time - game_duration) + ' min'
        }
      },
      getRawMinutesSinceStart(d){
        if(!d.solve_time || d.is_completed != 1 || !this.gameInstance.start_time) return null

        let start = new Date()
        start.setFullYear(this.gameInstance.start_time.split('T')[0].split('-')[0])
        start.setMonth(this.gameInstance.start_time.split('T')[0].split('-')[1] - 1, this.gameInstance.start_time.split('T')[0].split('-')[2])
        start.setHours(this.gameInstance.start_time.split('T')[1].split(':')[0])
        start.setMinutes(this.gameInstance.start_time.split('T')[1].split(':')[1])
        start.setSeconds(0)

        let time = d.solve_time
        if(!(d.solve_time instanceof Date) && d.solve_time && d.solve_time.includes('T')){
          time = new Date()
          time.setFullYear(d.solve_time.split('T')[0].split('-')[0])
          time.setMonth(d.solve_time.split('T')[0].split('-')[1] - 1, d.solve_time.split('T')[0].split('-')[2])
          time.setHours(d.solve_time.split('T')[1].split(':')[0])
          time.setMinutes(d.solve_time.split('T')[1].split(':')[1])
          time.setSeconds(0)
        }
        let minutes = Math.floor((time - start) / (1000 * 60))
        return minutes >= 0 ? minutes : null
      },
      getProgressPercent(d){
        let minutes = this.getRawMinutesSinceStart(d)
        if(minutes === null) return 0
        let duration = parseInt(this.gameInstance.duration_minutes)
        if(!duration || duration <= 0) return 0
        return Math.min(Math.round((minutes / duration) * 100), 100)
      },
      pacingChip(puzzle) {
        if (!this.pacingSummary) {
          return null
        }
        const entry = this.pacingSummary.perPuzzle[Number(puzzle.puzzle_id)]
        return entry && entry.delta !== null && entry.band !== 'overdue' ? entry : null
      },
      formatPaceDelta(delta) {
        return formatDelta(delta)
      },
      pacingTypical(puzzle) {
        if (!this.pacingSummary) {
          return ''
        }
        const entry = this.pacingSummary.perPuzzle[Number(puzzle.puzzle_id)]
        return entry && entry.typical !== null && entry.typical !== undefined ? formatDuration(entry.typical) : ''
      },
      getMinutesSinceStart(d){
        if(!this.gameInstance.start_time) return '-'

        let start = new Date()
        start.setFullYear(this.gameInstance.start_time.split('T')[0].split('-')[0])
        start.setMonth(this.gameInstance.start_time.split('T')[0].split('-')[1] - 1, this.gameInstance.start_time.split('T')[0].split('-')[2])
        start.setHours(this.gameInstance.start_time.split('T')[1].split(':')[0])
        start.setMinutes(this.gameInstance.start_time.split('T')[1].split(':')[1])
        start.setSeconds(0)

        let time = d.solve_time
        if(!(d.solve_time instanceof Date) && d.solve_time && d.solve_time.includes('T')){
          time = new Date()
          time.setFullYear(d.solve_time.split('T')[0].split('-')[0])
          time.setMonth(d.solve_time.split('T')[0].split('-')[1] - 1, d.solve_time.split('T')[0].split('-')[2])
          time.setHours(d.solve_time.split('T')[1].split(':')[0])
          time.setMinutes(d.solve_time.split('T')[1].split(':')[1])
          time.setSeconds(0)
        }
        var diffTime = (time - start)

        var total_minutes = parseInt(Math.floor(diffTime / (1000 * 60)));
        if (d.solve_time != null && d.is_completed == 1 && total_minutes >= 0) {
          return total_minutes + ' min'
        }
        return '-'
      },
      getDate(d){
        return this.getDayOfWeek(d) + formattedDate(this.date_format, d)
      },
      getTime(d){
        if(d instanceof Date){
          d = d.getFullYear() + '-' + (d.getMonth()+1) + '-' + d.getDate() + 'T' + d.getHours() + ':' + d.getMinutes()
        }
        return time(this.time_format, d)
      },
      getLocalDate(d){
        return formatGroupDate(d)
      },
      getLocalTime(d){
        return formatGroupTime(d)
      },
      getClueLabel(item){
        if(!item.clue) return ''
        if(item.type !== 'text' && item.clue.length > 0){
          return item.clue.split('/')[item.clue.split('/').length - 1]
        }
        return item.clue
      },
      getTotalTime(puzzles){
        let end_time = ''
        if (this.gameInstance.end_time == null){
          let index = puzzles.length
          // while (index-- && !puzzles[index].solve_time);
          while(index > 0){
            if(puzzles[index-1].is_completed == 1){
              end_time = puzzles[index-1]
              break
            }
            index--
          }
          return this.getMinutesSinceStart(end_time)
        } else {
          // new Date(null) is the Unix epoch, not Invalid Date — without this
          // guard a null start_time renders a ~56-year duration
          if(!this.gameInstance.start_time) return '-'
          end_time = new Date(this.gameInstance.end_time)
          var start = new Date(this.gameInstance.start_time)
          var diffTime = (end_time - start)
          var total_minutes = parseInt(Math.floor(diffTime / (1000 * 60)));
          return total_minutes + ' min'
        }


      },
      getPercentCompletion(puzzles){
        let completed = 0
        let total = puzzles.length
        let total_completion = 0

        for (let n in puzzles){
          if(puzzles[n].solve_time != null && puzzles[n].is_completed == 1){
            completed++
          }
        }

        total_completion = Math.floor((completed / total) * 100)

        return total_completion + '%'

      },
      getTotalClues(puzzles){
        let number_of_clues = 0
        if(puzzles.length > 0){
          for (let n in puzzles){
            number_of_clues = number_of_clues + parseInt(puzzles[n].hints_given_count)
          }
        } else {
          number_of_clues = this.gameInstance.number_of_hints
        }
        return number_of_clues
      },
      getTotalBreakages(puzzles){
        let number_of_breakages = 0
        if(puzzles.length > 0){
          for (let n in puzzles){
            number_of_breakages = number_of_breakages + parseInt(puzzles[n].breakage_occurred)
          }
        } else {
          number_of_breakages = this.gameInstance.number_of_breaks
        }
        return number_of_breakages

      },
      getType(p){
        if (p.is_completed == 1){
          return 'success'
        } else {
          return 'danger'
        }
      },
      async saveGameInstance() {
        if(!this.validGameInstanceFields()){
          return
        }
        this.editing.start_time.setFullYear(this.editing.start_date.getFullYear())
        this.editing.start_time.setMonth(this.editing.start_date.getMonth(), this.editing.start_date.getDate())

        this.editing.end_time.setFullYear(this.editing.end_date.getFullYear())
        this.editing.end_time.setMonth(this.editing.end_date.getMonth(), this.editing.end_date.getDate())

        this.editingGame = false
        this.saveInstance()
      },
      async savePuzzleInstance(){
        const puzzle = this.puzzleInstances[this.editingPuzzleIndex]
        this.errs.solve_date = ''
        this.errs.solve_time = ''

        if(puzzle && puzzle.is_completed){
          if(!puzzle.solve_time_d){
            this.errs.solve_date = 'Completion date is required'
            return
          }
          if(!puzzle.solve_time_t){
            this.errs.solve_time = 'Completion time is required'
            return
          }
        }

        this.editingPuzzleIndex = null

        for(let n in this.puzzleInstances){
          if(this.puzzleInstances[n].is_completed){
            this.puzzleInstances[n].solve_time = moment(this.puzzleInstances[n].solve_time_d, 'YYYY-MM-DD').format('YYYY-MM-DD')
              + 'T' + moment(this.puzzleInstances[n].solve_time_t, 'HH:mm:ss').format('HH:mm:ss')
          }

          this.puzzleInstances[n].breakage_occurred = 0
          if(this.puzzleInstances[n].breakage_description !== null && this.puzzleInstances[n].breakage_description !== ''){
            this.puzzleInstances[n].breakage_occurred = 1
          }
        }

        this.saveInstance()

      },
      validGameInstanceFields(){
        let errors = 0

        if(!this.editing.start_time){
          this.errs.start_time = 'Start time cannot be empty'
          errors++
        } else {
          this.errs.start_time = ''
        }
        if(!this.editing.end_time){
          this.errs.end_time = 'End time cannot be empty'
          errors++
        } else {
          this.errs.end_time = ''
        }
        if(!this.editing.booking_time){
          this.errs.booking_time = 'Booking time cannot be empty'
          errors++
        } else {
          this.errs.booking_time = ''
        }

        return errors > 0 ? false : true

      },
      async saveInstance(){
        this.loading = true
        var token = await this.getAuthToken()
        let config = { headers: { Authorization: token } }
        let companyId = localStorage.getItem('companyId')
        let gameInstanceId = this.$route.query.id
        let url = API_LOCATION + 'companies/' + companyId + '/games/instances/' + gameInstanceId

        let payload = {
          puzzles: this.puzzleInstances,
          game: this.gameInstance,
          booking_start_time: this.editing.booking_time ? this.editing.booking_time.getHours() + ':' + this.editing.booking_time.getMinutes() : this.gameInstance.booking_start_time,
          start_time: this.editing.start_time ? this.editing.start_time.getFullYear() + '-' + (this.editing.start_time.getMonth() + 1) + '-' + this.editing.start_time.getDate() + ' ' + this.editing.start_time.getHours() + ':' + this.editing.start_time.getMinutes() : this.gameInstance.start_time,
          end_time: this.editing.end_time ? this.editing.end_time.getFullYear() + '-' + (this.editing.end_time.getMonth() + 1) + '-' + this.editing.end_time.getDate() + ' ' + this.editing.end_time.getHours() + ':' + this.editing.end_time.getMinutes() : this.gameInstance.end_time,
          gm_id: this.selectedGameMaster.id
        }
        axios.put(url, payload, config)
          .then(response => {
            this.loading = false
            this.showAlert({ title: 'Success', message: 'Event form has been updated', variant: 'success' })
            this.getGameData()

          })
          .catch(error => {
            this.loading = false
            console.log(error)
            if(error.response.status == 401){
              this.$router.push('/forbidden')
            } else {
              this.showAlert({ title: 'Error', message: 'Something went wrong', variant: 'danger' })
            }
          })

      },
      handleUpload(){
        let file = event.target.files[0];
        this.uploaded_image = URL.createObjectURL(file);
        this.file = file
        this.$emit('change', file);
      },
      async getSignedURL (file) {
        let token = await this.getAuthToken()
        let config = {headers: {Authorization: token}}
        let url = API_LOCATION + 'upload/photos'

        let payload = {
          filePath: file.name,
          contentType: file.type
        }

        return new Promise ( function(resolve) {
          axios.post(url, payload, config)
            .then((res) => {
              resolve(res.data)
            })
            .catch((err) => {
              this.showAlert({
                title: 'Error',
                message: `You must upload a player photo before saving!`,
                variant: 'danger',
                confirmText: 'Try Again'
              })
              console.error(err)
            })
        })
      },
      async saveFile() {
        this.uploadUrl = await this.getSignedURL(this.file)
        let uploaded = await this.uploadFile(this.file)
      },
      uploadFile (file) {
        let url = this.uploadUrl
        let config = { headers: {
            'Content-Type': file.type
          }}

        let payload = file
        return new Promise ( function(resolve) {
          axios.put(url, payload, config)
            .then((res) => {
              resolve(res)
            })
            .catch((err) => {
              console.error(err)
            })
        })
      },
      async getLayout(){
        let img = new Image()
        img.src = URL.createObjectURL(this.file)


        return new Promise((resolve, reject) => {
          img.onload = () => resolve(img);
          img.onerror = reject;
        });

      },
      async savePhoto(){
        // Capture values immediately before any async operations to prevent race conditions
        // when localStorage changes due to group switching in another tab
        const companyId = localStorage.getItem('companyId');
        const groupId = JSON.parse(localStorage.getItem('group')).id;
        const bookingSlotId = this.gameInstance.booking_slot_id;
        const t = new Date().toLocaleString( 'sv' );

        let layout = await this.getLayout()
        if(parseFloat(layout.width / layout.height).toFixed(2) == 0.75 ) {
          this.layout = 'portrait'
        }
        this.loading = true
        await this.saveFile()
        var token = await this.getAuthToken()
        let config = {headers: {Authorization: token}}
        let url = API_LOCATION + 'companies/' + companyId + '/groups/' + groupId + '/photos?t=' + t
        if(bookingSlotId){
          url += '&bsId=' + bookingSlotId
        }

        let data = {
          uploaded_image:  this.uploadUrl.split("?")[0],
          layout: this.layout
        }

        axios.post(url, data, config)
          .then(response => {
            this.loading = false
            this.modals.upload_photo = false
            this.uploaded_image = 'https://console.offthecouch.io/img/placeholder.jpg'
            this.file = ''
            this.uploadUrl = ''
            this.showAlert({ title: 'Success', message: 'Photo has been uploaded.', variant: 'success' })
            this.getGameData()
          }).catch((err) => {
            console.log(err)
            this.loading = false
            if(err.response.status == 401){
              this.$router.push('/forbidden')
            } else {
              this.showAlert({
                title: 'Something went wrong!',
                variant: 'danger',
                confirmText: 'Try again'
              })
            }
        })
      },
      async getCompanyUsers(){
        this.loading = true
        let token = await this.getAuthToken()
        let config = { headers: { Authorization: token } }
        let companyId = localStorage.getItem('companyId')
        let url = API_LOCATION + 'companies/' + companyId + '/groups/' + this.gameInstance.company_group_id + '/company-users'

        axios.get(url, config)
          .then(response => {
            for(let n in response.data.company_users){
              if(response.data.company_users[n].first_name === null){
                response.data.company_users[n].first_name = ''
              }
              if(response.data.company_users[n].last_name === null){
                response.data.company_users[n].last_name = ''
              }
            }
            this.company_users = response.data.company_users
            this.loading = false
          })
          .catch(err => {
            this.loading = false
            if(err.response.status == 401){
              this.$router.push('/forbidden')
            } else {
              this.showAlert({ title: 'Error', message: 'Something went wrong', variant: 'danger' })
            }
          })
      },
      async confirmPhotoOptIn(recipients){
        // Warn (allow override) if any recipient opted out of photo sharing.
        let list = recipients || []
        let optedOut = list.filter(u => u && u.opted_out_photo_sharing)
        if(optedOut.length === 0){
          return true
        }
        let isSingle = list.length === 1
        let names = optedOut
          .map(u => `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email)
          .join(', ')
        let confirmed = await this.showAlert({
          title: isSingle ? 'Customer opted out' : 'Some participants opted out',
          message: isSingle
            ? `This customer opted out of having their photo shared. Only send if you have their consent.`
            : `${names} opted out of having their photo shared. Only send if you have their consent.`,
          variant: 'warning',
          showCancel: true,
          confirmText: 'Send anyway',
          cancelText: 'Cancel'
        })
        return confirmed === true
      },
      showAlert(opts){
        if(this.alertResolve){
          this.alertResolve(false)
        }
        this.alertDialog = {
          show: true,
          title: opts.title || '',
          message: opts.message || '',
          variant: opts.variant || 'warning',
          showCancel: opts.showCancel === true,
          confirmText: opts.confirmText || 'OK',
          cancelText: opts.cancelText || 'Cancel',
          confirmVariant: opts.confirmVariant || 'primary'
        }
        return new Promise(resolve => { this.alertResolve = resolve })
      },
      resolveAlert(value){
        this.alertDialog.show = false
        const resolve = this.alertResolve
        this.alertResolve = null
        if(resolve) resolve(value)
      },
      async emailPhoto(user){
        let recipients = user ? [user] : this.team
        if(!(await this.confirmPhotoOptIn(recipients))){
          return
        }
        this.loading = true
        var token = await this.getAuthToken()
        let config = {headers: {Authorization: token}}
        let companyId = localStorage.getItem('companyId')
        let companyGroupId = JSON.parse(localStorage.getItem('group')).id
        let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/photos/'+ this.photo.id + '/email-group'

        let u = []
        u.push(user)
        let data = {
          emails: user ?  u : this.team,
          link_type: user ? 0 : 1,
          link_id: this.photo.link_id,
          // One-shot send: never block on size, attach what fits and link the rest.
          allow_links: true
        }

        axios.post(url, data, config)
          .then(response => {
            this.loading = false
            this.$notify({
              type: 'success',
              message: 'Photo has been sent',
              icon: 'tim-icons icon-check-2'
            })
            this.getGameData()
          }).catch((err) => {
            console.log(err)
            this.loading = false
            if(err.response.status == 401){
              this.$router.push('/forbidden')
            } else if(isNoRecipientsError(err)){
              this.showAlert({ title: 'No one to email', message: NO_RECIPIENTS_MESSAGE, variant: 'warning' })
            } else {
              this.$notify({
                type: 'danger',
                message: 'Failed to send photo. Please try again.',
                icon: 'tim-icons icon-alert-circle-exc'
              })
            }
        })

      },
      async emailSelectedPhotos(){
        let count = this.selectedPhotos.length
        // Fold the opt-out warning into this one dialog (firing a second sweetalert right after
        // this one closes gets auto-dismissed, which swallowed the prompt).
        let optedOut = (this.team || []).filter(u => u && u.opted_out_photo_sharing)
        let warnText = ''
        if(optedOut.length > 0){
          let names = optedOut
            .map(u => `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email)
            .join(', ')
          warnText = this.team.length === 1
            ? ' This customer opted out of having their photo shared. Only send if you have their consent.'
            : ` ${names} opted out of having their photo shared. Only send if you have their consent.`
        }
        let confirmed = await this.showAlert({
          title: `Email ${count === 1 ? 'photo' : count + ' photos'}?`,
          message: `This will send ${count} ${count === 1 ? 'photo' : 'photos'} to all ${this.team.length} team members in a single email. Photos too large to attach arrive as download links.` + warnText,
          variant: optedOut.length > 0 ? 'warning' : 'info',
          showCancel: true,
          confirmText: optedOut.length > 0 ? 'Send anyway' : 'Send'
        })

        if (confirmed !== true) return

        this.emailing_all_photos = true
        var token = await this.getAuthToken()
        let config = {headers: {Authorization: token}}
        let companyId = localStorage.getItem('companyId')
        let companyGroupId = JSON.parse(localStorage.getItem('group')).id
        // Use first selected photo's path param for backward-compatible URL, pass selected IDs in body
        let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/photos/'+ this.selectedPhotos[0].id + '/email-group'

        let data = {
          photoIds: this.selectedPhotos.map(p => p.id),
          link_ids: this.selectedPhotos.map(p => p.link_id).filter(id => id != null),
          emails: this.team,
          link_type: 1,
          link_id: this.selectedPhotos[0].link_id,
          // One-shot send: never block on size, attach what fits and link the rest.
          allow_links: true
        }

        axios.post(url, data, config)
          .then(response => {
            this.emailing_all_photos = false
            this.$notify({
              type: 'success',
              message: `${count} ${count === 1 ? 'photo has' : 'photos have'} been sent`,
              icon: 'tim-icons icon-check-2'
            })
            this.getGameData()
          }).catch((err) => {
            console.log(err)
            this.emailing_all_photos = false
            if(err.response.status == 401){
              this.$router.push('/forbidden')
            } else if(isNoRecipientsError(err)){
              this.showAlert({ title: 'No one to email', message: NO_RECIPIENTS_MESSAGE, variant: 'warning' })
            } else {
              this.$notify({
                type: 'danger',
                message: 'Failed to send photos. Please try again.',
                icon: 'tim-icons icon-alert-circle-exc'
              })
            }
        })
      }
    },
    async beforeMount() {
      if(!(_.find(JSON.parse(localStorage.getItem('features')), {id: 5}))){
        this.photos_not_enabled = true
      } else {
        this.photos_not_enabled = false
      }
      this.date_format_cal = this.date_format
      this.date_format_cal =  this.date_format_cal.replaceAll('Y', 'y')
      this.date_format_cal = this.date_format_cal.replaceAll('D', 'd')
      if(JSON.parse(localStorage.getItem('group')).show_day_of_week){
        this.day_of_week = 'ddd, '
      }
      if (this.time_format === 'HH:mm') {
        this.picker_time_format = 'HH:mm'
      }
      await this.getGameData()
    }
  }
</script>

<style scoped>
  .fixed-size-img {
    width: 100%; /* width of container */
    height: 130px; /* height of container */
    object-fit: cover;
  }
  .gi-photo-thumbs {
    display: flex;
    gap: 4px;
    overflow-x: auto;
    padding: 8px 0 4px;
  }
  .gi-photo-thumb {
    position: relative;
    flex-shrink: 0;
    width: 56px;
    height: 56px;
    border-radius: 4px;
    overflow: hidden;
    cursor: pointer;
    border: 2px solid transparent;
    opacity: 0.6;
    transition: opacity 0.15s, border-color 0.15s;
  }
  .gi-photo-thumb:hover {
    opacity: 0.85;
  }
  .gi-photo-thumb--active {
    border-color: #e14eca;
    opacity: 1;
  }
  .gi-photo-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .gi-photo-thumb__check {
    position: absolute;
    top: 3px;
    left: 3px;
    width: 20px;
    height: 20px;
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.7);
    border: 2px solid rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
  }
  .gi-photo-thumb__check:hover {
    border-color: #e14eca;
  }
  .gi-photo-thumb__check i {
    font-size: 12px;
    color: #fff;
    font-weight: bold;
  }
  .gi-photo-thumb__check--selected {
    background: #e14eca;
    border-color: #e14eca;
  }

  /* Lightbox */
  .gi-lightbox {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.6);
    border-radius: 4px;
    overflow: hidden;
    min-height: 300px;
  }
  .gi-lightbox__img {
    max-width: 100%;
    max-height: 70vh;
    object-fit: contain;
    display: block;
  }
  .gi-lightbox__loader {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.3);
  }
  .gi-lightbox__nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: none;
    background: rgba(0, 0, 0, 0.6);
    color: #fff;
    font-size: 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s;
    z-index: 2;
  }
  .gi-lightbox__nav:hover {
    background: rgba(225, 78, 202, 0.7);
  }
  .gi-lightbox__nav--prev {
    left: 12px;
  }
  .gi-lightbox__nav--next {
    right: 12px;
  }
  .gi-lightbox__bar {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
    z-index: 2;
  }
  .gi-lightbox__counter {
    color: rgba(255, 255, 255, 0.8);
    font-size: 13px;
    font-weight: 500;
  }
  .gi-lightbox__close {
    background: rgba(255, 255, 255, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.3);
    color: #fff;
    font-size: 12px;
    padding: 4px 12px;
    border-radius: 4px;
    cursor: pointer;
    transition: background 0.15s;
  }
  .gi-lightbox__close:hover {
    background: rgba(255, 255, 255, 0.25);
  }

  /* Fade transition for photo switching */
  .gi-lightbox-fade-enter-active,
  .gi-lightbox-fade-leave-active {
    transition: opacity 0.2s ease;
  }
  .gi-lightbox-fade-enter,
  .gi-lightbox-fade-leave-to {
    opacity: 0;
  }
</style>

<style>
@import './game-instance.css';
</style>
